import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

// eslint-disable-next-line no-unused-vars -- used by the hidden view-count pill below
import { Icon } from 'components';
import { formatCompactNumber } from 'lib';
import { TIRI_CREATOR } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${TIRI_CREATOR.ROW_GAP}px;
  min-width: 0;
`;

const User = styled.div`
  display: flex;
  align-items: center;
  gap: ${TIRI_CREATOR.NAME_GAP}px;
  min-width: 0;
`;

/**
 * The ringed avatar, falling back to initials on mobile's teal disc.
 *
 * The fallback is not decoration: `avatar` is genuinely optional on `User`, and an account
 * without one would otherwise render as an empty ring. `box-sizing: border-box` keeps the ring
 * inside the 36px box, as mobile's wrapper does.
 */
const Avatar = styled.div`
  width: ${TIRI_CREATOR.AVATAR_SIZE}px;
  height: ${TIRI_CREATOR.AVATAR_SIZE}px;
  box-sizing: border-box;
  flex: none;
  border-radius: 50%;
  border: ${TIRI_CREATOR.AVATAR_RING}px solid ${TIRI_CREATOR.AVATAR_RING_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-family: CeraPro, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: ${TIRI_CREATOR.AVATAR_FALLBACK_TEXT};
  background: ${(props) =>
    props.$src ? `url(${props.$src}) center / cover no-repeat` : TIRI_CREATOR.AVATAR_FALLBACK_BG};
  /* The fill sits inside the ring, not under it. */
  background-clip: padding-box;
`;

const Name = styled.span`
  font-family: CeraPro, sans-serif;
  font-size: ${TIRI_CREATOR.NAME_SIZE}px;
  font-weight: 900;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

/** FanCam's `PointsPill`, at its `small` size. */
const Points = styled.span`
  flex: none;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid ${TIRI_CREATOR.POINTS_BORDER};
  background: ${TIRI_CREATOR.PILL_BG};
  font-family: CeraPro, sans-serif;
  font-size: ${TIRI_CREATOR.POINTS_SIZE}px;
  font-weight: 900;
  color: #fff;
  white-space: nowrap;
`;

// eslint-disable-next-line no-unused-vars -- the hidden view-count pill's style, kept for switch-back
const Views = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${TIRI_CREATOR.PILL_BG};
  font-family: CeraPro, sans-serif;
  font-size: ${TIRI_CREATOR.VIEW_SIZE}px;
  color: #fff;
  white-space: nowrap;
`;

/** First letters of up to two words — mobile's `getInitials`. */
const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

/**
 * The creator's attribution: ringed avatar, bold name, points pill, view-count pill.
 *
 * A PORT of mobile's `TiriContentCreatorInfoOverlay` (itself FanCam's player creator row at ~80%
 * size) so the two surfaces can be diffed. Sits at the BOTTOM of the card, directly above the
 * tiles and outside their slab — see `RateItFeedBottomStack`.
 *
 * Name before username, FanCam's order, with no "@". Points and views both render as 0 rather
 * than hiding when the wire omits them, so the row does not jump between items.
 */
const RateItCreatorRow = ({ creator, viewCount }) => {
  const { t } = useTranslation();
  const name = creator?.name || creator?.username || t('tiri.anonymousFan', 'fan');
  // eslint-disable-next-line no-unused-vars -- read by the hidden view-count pill below
  const views = formatCompactNumber(viewCount ?? 0);

  return (
    <Row>
      <User>
        <Avatar $src={creator?.avatarUrl}>{creator?.avatarUrl ? null : initialsOf(name)}</Avatar>
        <Name>{name}</Name>
      </User>

      <Points>{formatCompactNumber(creator?.points ?? 0)} pts</Points>

      {/* View-count pill HIDDEN for now (Workstream 8: "remove view count icon from template"),
          matching mobile. Only the UI is off — `viewCount` is still passed in and views are still
          tracked, so uncommenting this block brings it straight back.
      <Views aria-label={t('tiri.viewCount', '{{views}} views', { views })}>
        <Icon type="visibleOn" dimensions={TIRI_CREATOR.VIEW_ICON} col="#fff" />
        {views}
      </Views>
      */}
    </Row>
  );
};

export default RateItCreatorRow;
